import { animationTransition } from '@shared/utils/animationTransition';
import { Box } from '@withjoy/joykit';
import type { RefObject } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VimeoContainer } from '../../CardTileConcierge';
import { useVimeoPlayerContext } from '@apps/registry/common/components/Catalog/utils/vimeoPlayer';
import type Player from '@vimeo/player';
import type { CarouselSlideBaseProps } from './Carousel';
import { usePauseOffScreen } from '../../utils/vimeo/usePauseOffScreen';
import { useAutoplayValues } from '../../../hooks/useAutoplayValues';
import type { PropContent } from '../../../../../propValues.types';
import { noop } from 'lodash-es';
import { getLocalStorage } from '@shared/core/storage';

// Shadow the global: the bare `localStorage` getter throws SecurityError when the browser
// blocks storage (Safari "Block all cookies", opaque-origin iframes). See PRNT-4831.
const localStorage = getLocalStorage();

type CarouselVideoContainerProps = CarouselSlideBaseProps & { asset: PropContent & { type: 'video' } };

const getHasPlayedKey = (prop: PropContent & { type: 'video' }) => `stationery-prop-seen:${prop.videoUrl}`;

const useVideoAssetUrl = (asset: PropContent & { type: 'video' }) =>
  useMemo(() => {
    const url = new URL(asset.videoUrl);
    const searchParams = url.searchParams;
    searchParams.append('muted', '1');
    searchParams.append('loop', '1');
    searchParams.append('background', '1');
    // We start the video manually
    searchParams.append('autoplay', '0');
    return url.href;
  }, [asset]);

const useVideoPlayer = (r: RefObject<HTMLIFrameElement>) => {
  const [player, setPlayer] = useState<Player | null>(null);
  const { VimeoPlayer, prepareForUse } = useVimeoPlayerContext();

  useEffect(prepareForUse, [prepareForUse]);

  useEffect(() => {
    if (VimeoPlayer && r.current) {
      setPlayer(new VimeoPlayer(r.current));
    }
  }, [VimeoPlayer, r]);

  if (VimeoPlayer && player) {
    return { isReady: true, player } as const;
  }

  return { isReady: false, player: null } as const;
};

const ContentCarouselVideo = (props: Pick<CarouselVideoContainerProps, 'asset'>) => {
  const { asset } = props;

  const playerRef = useRef<HTMLIFrameElement>(null);
  const videoPlayer = useVideoPlayer(playerRef);

  const { videoPropsAutoplay } = useAutoplayValues();

  // all videos are paused offscreen, played again on mouse enter or by 'useEffect'
  const [videoContainerRef, isInView] = usePauseOffScreen(noop, () => videoPlayer.player?.pause().catch(noop));

  useEffect(() => {
    if (videoPlayer.isReady && isInView && videoPropsAutoplay) {
      if (asset.once) {
        const hasAlreadyPlayed = Boolean(localStorage.getItem(getHasPlayedKey(asset)));
        // if the video is "once" and has not been played yet, play it
        if (!hasAlreadyPlayed) {
          videoPlayer.player?.play().catch(noop);
          // mark the video as played so we don't autoplay again
          localStorage.setItem(getHasPlayedKey(asset), 'true');
        }

        // if the "once" video has already been played, don't autoplay again (played again on mouse enter)
        return;
      }

      // other wise, autoplay
      videoPlayer.player?.play().catch(noop);
    }
  }, [asset, asset.once, isInView, videoPlayer, videoPropsAutoplay]);

  // all paused videos can be played on mouse enter
  const handleMouseEnter = useCallback(() => {
    if (videoPlayer.isReady) {
      videoPlayer.player?.play().catch(noop);
    }
  }, [videoPlayer]);

  const videoUrl = useVideoAssetUrl(asset);

  const { videoThumbnailUrl, backgroundColor } = asset;

  return (
    <Box
      ref={videoContainerRef}
      width="100%"
      height="100%"
      overflow="hidden"
      position="relative"
      onMouseEnter={handleMouseEnter}
      backgroundColor={backgroundColor ?? 'hsla(45, 12%, 93%, 1)'}
      backgroundPosition="center"
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundImage={videoThumbnailUrl ? `url('${videoThumbnailUrl}')` : 'unset'}
    >
      <VimeoContainer
        r={playerRef}
        src={videoUrl!}
        cover
      />
    </Box>
  );
};

export const CarouselVideoContainer = (props: CarouselVideoContainerProps) => {
  const { onClick, currentPosition, index } = props;

  const isShowing = currentPosition === 0;

  return (
    <Box
      onClick={() => onClick?.(index)}
      zIndex={3}
      cursor={onClick !== undefined ? 'pointer' : undefined}
      width="100%"
      height="100%"
      position="absolute"
      style={{ transition: animationTransition('transform'), transform: `translateX(${100 * currentPosition}%)` }}
    >
      {isShowing && <ContentCarouselVideo {...props} />}
    </Box>
  );
};
